'use client';

import { Check } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { BentoGrid, type BentoCard } from '@/components/sections/bento-grid';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { BubbleCall, SubpageHero } from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

/* -------------------------------------------------------------------------- */
/*  Bento — the headline capability clusters                                  */
/* -------------------------------------------------------------------------- */

const BENTO_CARDS_EN: BentoCard[] = [
  {
    id: 'ai-receptionist',
    emoji: '🤖',
    title: 'An AI that actually answers',
    description:
      'Every call picked up on the first ring by a voice that sounds like a real teammate — it captures the reason, resolves what it can, and warm-transfers the rest with full context.',
    image: 'https://platform.open.cx/changelog/phone-agent-workflows.jpg',
    layout: 'horizontal',
    modal: {
      eyebrow: 'AI receptionist · 24/7',
      headline: 'A round-the-clock AI receptionist on every line',
      paragraphs: [
        "When your team can't pick up — after hours, at capacity, or all on other calls — Open answers instead. It greets the caller, understands intent, captures the details, and either resolves the request end-to-end or routes it to the right person.",
        "No 'press 1 for sales'. A natural conversation that gets the caller where they need to go, then logs everything to your CRM automatically.",
      ],
      bullets: [
        '24/7 autonomous answering — never miss a call again',
        'Captures caller, reason, and details on every call',
        'Resolves routine requests without a human',
        'Intelligent escalation with the full transcript attached',
      ],
    },
  },
  {
    id: 'numbers',
    emoji: '🌍',
    title: 'Numbers in 100+ countries',
    description:
      'Local, toll-free, and vanity numbers provisioned in minutes — or keep the numbers you already have. No porting headaches, no per-seat fees.',
    layout: 'vertical',
    modal: {
      eyebrow: 'Phone numbers',
      headline: 'Get the right number, anywhere',
      paragraphs: [
        'Spin up local numbers in 100+ countries, memorable vanity numbers, and toll-free lines from one dashboard. Bring your existing numbers and keep them exactly where they are.',
      ],
      bullets: [
        'Local numbers in 100+ countries',
        'Toll-free and vanity numbers',
        'Bring-your-own-number — no porting required',
        'Provisioned in minutes from the dashboard',
      ],
    },
  },
  {
    id: 'routing',
    emoji: '🔀',
    title: 'Smart routing & IVR',
    description:
      'Visual call-flow builder, auto-attendant menus, business-hours rules, and skills-based routing — so every call lands in the right place.',
    layout: 'vertical',
    modal: {
      eyebrow: 'Call routing',
      headline: 'Route every call with a drag-and-drop flow builder',
      paragraphs: [
        "Design IVR menus, ring groups, business-hours rules, and overflow paths visually — no telecom engineer required. Open's AI can replace the menu entirely, listening for intent and routing on the fly.",
      ],
      bullets: [
        'Drag-and-drop call-flow builder',
        'IVR / auto-attendant menus',
        'Skills-based and round-robin routing',
        'Business hours, holidays, and overflow rules',
      ],
    },
  },
  {
    id: 'recording-transcription',
    emoji: '🎙️',
    title: 'Recording & live transcription',
    description:
      'Every call recorded, transcribed in real time, and analyzed for sentiment and topics — searchable forever, summarized the moment you hang up.',
    layout: 'vertical',
    modal: {
      eyebrow: 'Recording & AI analysis',
      headline: 'Recording, transcription, and analysis on every call',
      paragraphs: [
        'Calls and voicemails are recorded and transcribed automatically. AI tags sentiment and topics live, suggests responses to agents mid-call, and drops a clean summary into your CRM the second the call ends.',
      ],
      bullets: [
        'Automatic call recording',
        'Real-time transcription',
        'Voicemail-to-text transcription',
        'Live sentiment & topic analysis with post-call summaries',
      ],
    },
  },
  {
    id: 'messaging',
    emoji: '💬',
    title: 'Calls and SMS, one inbox',
    description:
      'Two-way business texting sits beside your calls, voicemails, and contacts in a single shared inbox the whole team can see.',
    layout: 'vertical',
    modal: {
      eyebrow: 'Messaging',
      headline: 'Voice and text in one unified inbox',
      paragraphs: [
        'Send and receive SMS from your business numbers, and manage it next to every call and voicemail in one shared inbox. Nothing falls through the cracks, and the whole team sees the full history.',
      ],
      bullets: [
        'Two-way SMS / business texting',
        'Unified inbox for calls, texts, voicemails, and contacts',
        'Shared conversation history across the team',
        'AI-drafted replies and automated follow-ups',
      ],
    },
  },
  {
    id: 'analytics',
    emoji: '📊',
    title: 'Analytics that spot problems early',
    description:
      'A live dashboard of volume, wait times, resolution, and CSAT — surfacing issues before they cost you a customer.',
    layout: 'vertical',
    modal: {
      eyebrow: 'Analytics & reporting',
      headline: 'Know what every call is telling you',
      paragraphs: [
        'Track call volume, missed calls, wait times, talk time, resolution rate, and CSAT in real time. Per-agent and per-team views make it obvious where to coach and where demand is spiking.',
      ],
      bullets: [
        'Real-time performance dashboard',
        'Per-agent and per-team metrics',
        'Missed-call, wait-time, and resolution tracking',
        'Trend alerts that surface issues early',
      ],
    },
  },
  {
    id: 'integrations',
    emoji: '🔌',
    title: '100+ integrations & open API',
    description:
      'Native sync with Salesforce, HubSpot, Pipedrive, Zendesk and 100+ tools — plus a full REST API and MCP for anything custom.',
    layout: 'vertical',
    modal: {
      eyebrow: 'Integrations & developers',
      headline: 'Plugs into the stack you already run on',
      paragraphs: [
        'Every call, text, and recording syncs automatically to your CRM and helpdesk. When you need something bespoke, the REST API and MCP server let you wire Open into any workflow.',
      ],
      bullets: [
        'Native CRM sync: Salesforce, HubSpot, Pipedrive, Zoho',
        'Helpdesk integrations: Zendesk, Intercom, and more',
        'Slack, Zapier, and Make automations',
        'Full REST API + MCP server for custom builds',
      ],
    },
  },
];

const BENTO_CARDS_AR: BentoCard[] = [
  {
    id: 'ai-receptionist',
    emoji: '🤖',
    title: 'AI يرفع السمّاعة فعلاً',
    description:
      'من أول رنة يرد صوت تحسّه زميل من الفريق — يعرف سبب المكالمة، ويحل ما يقدر عليه، ويحوّل الباقي ومعه التفاصيل كاملة.',
    image: 'https://platform.open.cx/changelog/phone-agent-workflows.jpg',
    layout: 'horizontal',
    modal: {
      eyebrow: 'موظف استقبال AI · ٢٤/٧',
      headline: 'موظف استقبال AI على كل خط، وما ينام',
      paragraphs: [
        'بعد الدوام، أو في وقت الذروة، أو لمّا يكون الفريق كله على خطوط أخرى — Open يرد بدلاً عنهم. يرحّب بالمتصل، ويفهم ما يحتاجه، ويسجّل التفاصيل، ثم إما ينهي الطلب من بدايته إلى نهايته أو يوجّهه للشخص المناسب.',
        'بدون «اضغط ١ للمبيعات». محادثة طبيعية توصل المتصل لوجهته من أول مرة، وتسجّل كل شيء في الـ CRM عندك تلقائياً.',
      ],
      bullets: [
        'رد ذاتي ٢٤/٧ — ما تفوتك مكالمة',
        'يسجّل المتصل وسبب مكالمته وتفاصيلها في كل مرة',
        'ينهي الطلبات الروتينية بلا تدخل بشري',
        'تصعيد ذكي، ومعه نص المحادثة كاملاً',
      ],
    },
  },
  {
    id: 'numbers',
    emoji: '🌍',
    title: 'أرقام في +١٠٠ دولة',
    description:
      'أرقام محلية ومجانية ومميزة تجهّزها في دقائق — أو أبقِ أرقامك كما هي. بدون تعقيدات النقل، وبدون رسوم على كل موظف.',
    layout: 'vertical',
    modal: {
      eyebrow: 'أرقام الهاتف',
      headline: 'الرقم اللي تحتاجه، في أي بلد',
      paragraphs: [
        'جهّز أرقام محلية في +١٠٠ دولة، وأرقام مميزة يحفظها العميل من أول مرة، وخطوط مجانية — كلها من لوحة تحكم وحدة. وأرقامك الحالية؟ تبقى في مكانها بالضبط.',
      ],
      bullets: [
        'أرقام محلية في +١٠٠ دولة',
        'أرقام مجانية ومميزة',
        'استخدم رقمك الحالي — بدون نقل',
        'جهّزه في دقائق من لوحة التحكم',
      ],
    },
  },
  {
    id: 'routing',
    emoji: '🔀',
    title: 'توجيه ذكي وIVR',
    description:
      'محرّر مرئي لمسار المكالمة، وقوائم رد آلي، وقواعد ساعات دوام، وتوجيه حسب المهارة — كل مكالمة توصل لوجهتها الصحيحة.',
    layout: 'vertical',
    modal: {
      eyebrow: 'توجيه المكالمات',
      headline: 'وجّه كل مكالمة بالسحب والإفلات',
      paragraphs: [
        'صمّم قوائم IVR، ومجموعات الرنين، وقواعد ساعات الدوام، ومسارات الفائض — كلها بصرياً، وبدون مهندس اتصالات. وإذا أردت، يستغني الـ AI في Open عن القائمة كلها: يستمع لنية المتصل ويوجّهه فوراً.',
      ],
      bullets: [
        'محرّر مسار مكالمات بالسحب والإفلات',
        'قوائم IVR / رد آلي',
        'توجيه حسب المهارة وبالتناوب',
        'ساعات الدوام والإجازات الرسمية وقواعد الفائض',
      ],
    },
  },
  {
    id: 'recording-transcription',
    emoji: '🎙️',
    title: 'تسجيل ونسخ لحظي',
    description:
      'يسجّل كل مكالمة وينسخها نصاً لحظياً، ويحلّل النبرة والمواضيع — تبحث فيها متى شئت، والملخّص جاهز فور انتهاء المكالمة.',
    layout: 'vertical',
    modal: {
      eyebrow: 'التسجيل وتحليل AI',
      headline: 'كل مكالمة: تسجيل، نص، وتحليل',
      paragraphs: [
        'يسجّل المكالمات والرسائل الصوتية وينسخها نصاً تلقائياً. والـ AI ما يتوقف عند هذا الحد: يوسم النبرة والمواضيع لحظياً، ويقترح على الموظف رداً وهو على الخط، ويضع ملخصاً واضحاً في الـ CRM عندك فور انتهاء المكالمة.',
      ],
      bullets: [
        'تسجيل مكالمات تلقائي',
        'نسخ لحظي',
        'تحويل الرسائل الصوتية إلى نص',
        'تحليل نبرة ومواضيع لحظي، وملخّص بعد كل مكالمة',
      ],
    },
  },
  {
    id: 'messaging',
    emoji: '💬',
    title: 'مكالمات وSMS في صندوق واحد',
    description:
      'رسائل SMS ثنائية إلى جانب مكالماتك وبريدك الصوتي وجهات اتصالك — صندوق واحد، يراه الفريق كله.',
    layout: 'vertical',
    modal: {
      eyebrow: 'المراسلة',
      headline: 'الصوت والنص، صندوق واحد',
      paragraphs: [
        'أرسل واستقبل SMS من أرقام شركتك، وتابعها إلى جانب كل مكالمة ورسالة صوتية في صندوق واحد مشترك. ما في شي يضيع في الطريق، والسجل كامل أمام الفريق.',
      ],
      bullets: [
        'SMS ثنائي / مراسلة نصية للأعمال',
        'صندوق واحد للمكالمات والرسائل والبريد الصوتي وجهات الاتصال',
        'سجل محادثات مشترك بين الفريق كله',
        'ردود يكتبها AI ومتابعات تلقائية',
      ],
    },
  },
  {
    id: 'analytics',
    emoji: '📊',
    title: 'تحليلات تكشف المشاكل مبكراً',
    description:
      'لوحة لحظية: حجم المكالمات، وقت الانتظار، نسبة الحل، وCSAT — تشوف المشكلة قبل ما تكلّفك عميلاً.',
    layout: 'vertical',
    modal: {
      eyebrow: 'التحليلات والتقارير',
      headline: 'اعرف وش تقوله لك كل مكالمة',
      paragraphs: [
        'تابع حجم المكالمات، والفائتة منها، وأوقات الانتظار، ووقت الكلام، ونسبة الحل، وCSAT — كلها لحظياً. وطرق العرض لكل موظف ولكل فريق تبيّن لك وين تحتاج تدريب ووين يرتفع الطلب.',
      ],
      bullets: [
        'لوحة أداء لحظية',
        'مقاييس لكل موظف ولكل فريق',
        'تتبّع المكالمات الفائتة ووقت الانتظار ونسبة الحل',
        'تنبيهات على الاتجاهات تكشف المشكلة مبكراً',
      ],
    },
  },
  {
    id: 'integrations',
    emoji: '🔌',
    title: '+١٠٠ تكامل وAPI مفتوح',
    description:
      'مزامنة أصلية مع Salesforce، HubSpot، Pipedrive، Zendesk، و+١٠٠ أداة — مع REST API كامل وMCP لأي شيء مخصص.',
    layout: 'vertical',
    modal: {
      eyebrow: 'التكاملات والمطوّرون',
      headline: 'يندمج مع الأدوات اللي تشتغل عليها أصلاً',
      paragraphs: [
        'كل مكالمة ورسالة وتسجيل تتزامن تلقائياً مع الـ CRM ونظام الدعم عندك. وإذا احتجت شي خارج الجاهز، فإن REST API وMCP server يربطان Open بأي سير عمل عندك.',
      ],
      bullets: [
        'مزامنة CRM أصلية: Salesforce، HubSpot، Pipedrive، Zoho',
        'تكاملات أنظمة الدعم: Zendesk، Intercom، وغيرها',
        'أتمتة Slack، Zapier، Make',
        'REST API كامل + MCP server للبناء المخصص',
      ],
    },
  },
];

/* -------------------------------------------------------------------------- */
/*  Complete capability checklist — grouped, exhaustive                       */
/* -------------------------------------------------------------------------- */

const CAPABILITY_GROUPS_EN: { title: string; items: string[] }[] = [
  {
    title: 'Calling',
    items: [
      'Inbound & outbound voice',
      'Call recording',
      'Call forwarding & transfer',
      'Warm transfer with context',
      'IVR / auto-attendant',
      'Visual call-flow builder',
      'Ring groups & skills-based routing',
      'Business-hours & holiday rules',
      'High concurrency — 500+ simultaneous calls',
    ],
  },
  {
    title: 'Numbers',
    items: [
      'Local numbers in 100+ countries',
      'Toll-free numbers',
      'Vanity numbers',
      'Shared team numbers',
      'Bring your own number (no porting)',
      'SIP trunking & API-first provisioning',
    ],
  },
  {
    title: 'AI',
    items: [
      '24/7 AI receptionist',
      'Autonomous resolution of routine calls',
      'Live transcription',
      'Sentiment & topic analysis',
      'Suggested responses for agents',
      'Real-time manager coaching',
      'Post-call summaries',
      'Automated follow-ups',
      'Custom & cloned voices',
      'Multi-lingual conversations',
    ],
  },
  {
    title: 'Messaging & inbox',
    items: [
      'Two-way SMS / business texting',
      'Voicemail',
      'Voicemail transcription',
      'Unified inbox for calls, texts & contacts',
      'Shared conversation history',
    ],
  },
  {
    title: 'Collaboration',
    items: [
      'Tasks created from conversations',
      'Notes & @mentions on calls',
      'Real-time notifications',
      'Shared team inboxes',
      'Manager visibility & live coaching',
    ],
  },
  {
    title: 'Analytics, integrations & security',
    items: [
      'Real-time analytics dashboard',
      'Per-agent & per-team metrics',
      'Native CRM & helpdesk sync',
      '100+ app integrations',
      'REST API & MCP server',
      'SOC 2 · GDPR · HIPAA compliant',
    ],
  },
];

const CAPABILITY_GROUPS_AR: { title: string; items: string[] }[] = [
  {
    title: 'المكالمات',
    items: [
      'مكالمات صوتية واردة وصادرة',
      'تسجيل المكالمات',
      'تحويل وإعادة توجيه المكالمات',
      'تحويل مع تسليم التفاصيل',
      'IVR / رد آلي',
      'محرّر مرئي لمسار المكالمات',
      'مجموعات رنين وتوجيه حسب المهارة',
      'قواعد ساعات الدوام والإجازات',
      'تزامن عالي — +٥٠٠ مكالمة متزامنة',
    ],
  },
  {
    title: 'الأرقام',
    items: [
      'أرقام محلية في +١٠٠ دولة',
      'أرقام مجانية',
      'أرقام مميزة',
      'أرقام فريق مشتركة',
      'استخدم رقمك الحالي (بدون نقل)',
      'SIP trunking وتجهيز أساسه API',
    ],
  },
  {
    title: 'AI',
    items: [
      'موظف استقبال AI ٢٤/٧',
      'حل ذاتي للمكالمات الروتينية',
      'نسخ لحظي',
      'تحليل نبرة ومواضيع',
      'ردود مقترحة للموظفين',
      'توجيه لحظي من المدير',
      'ملخصات بعد المكالمة',
      'متابعات تلقائية',
      'أصوات مخصصة ومستنسخة',
      'محادثات بعدة لغات',
    ],
  },
  {
    title: 'المراسلة وصندوق الوارد',
    items: [
      'SMS ثنائي / مراسلة نصية للأعمال',
      'بريد صوتي',
      'نسخ البريد الصوتي نصاً',
      'صندوق موحّد للمكالمات والرسائل وجهات الاتصال',
      'سجل محادثات مشترك',
    ],
  },
  {
    title: 'التعاون',
    items: [
      'مهام تتولّد من المحادثات',
      'ملاحظات وإشارات (@) على المكالمات',
      'إشعارات لحظية',
      'صناديق وارد مشتركة للفرق',
      'إشراف المدير وتوجيه لحظي',
    ],
  },
  {
    title: 'التحليلات والتكاملات والأمان',
    items: [
      'لوحة تحليلات لحظية',
      'مقاييس لكل موظف ولكل فريق',
      'مزامنة أصلية مع CRM ونظام الدعم',
      '+١٠٠ تكامل مع تطبيقات',
      'REST API وMCP server',
      'متوافق مع SOC 2 · GDPR · HIPAA',
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

const CONTENT = {
  en: {
    heroTitle: 'The AI phone system that does it all',
    heroSubtitleA:
      'Every feature of a modern business phone — calls, numbers, IVR, SMS, recording, analytics.',
    heroSubtitleB:
      'With an AI that actually picks up, resolves, and routes the call.',
    worksWithLabel: 'Syncs with',
    bubbleTopic: 'Booking change · resolving',
    bentoTitle:
      'Everything a modern phone system does — and an AI that handles the call.',
    bentoSubtitle:
      "Open Telephony has every feature you'd expect from a business phone platform, plus an AI receptionist that answers, resolves, and routes around the clock.",
    benefitsTitle: 'The AI voice your phone system has been missing',
    benefitsSubtitle:
      'Natural conversations, in any language, on every line — powered by Agent 5.',
    benefits: [
      {
        title: 'International Numbers',
        description:
          'Local phone numbers in 100+ countries. No international fees for your customers, no porting for you.',
      },
      {
        title: 'Custom Voices and Voice Cloning',
        description:
          'Clone your brand voice or build a custom one. Your AI sounds exactly the way you want it to.',
      },
      {
        title: 'High Concurrency',
        description:
          'Handle up to 500 calls at once. No busy signals, no hold music, instant scaling at your busiest hour.',
      },
      {
        title: 'Multi-lingual Support',
        description:
          'Speak to every caller in their language, with native pronunciation across 6+ languages.',
      },
      {
        title: 'SIP Trunking and API First',
        description:
          'Connect via SIP trunking or API. Works with any phone system, or build fully custom call workflows.',
      },
    ],
    checklistEyebrow: 'Complete feature set',
    checklistTitle: 'Every feature, in one phone system.',
    checklistSubtitle:
      "The complete checklist — everything you'd find across the best business phone tools, unified under one AI-native platform.",
    faqTitle: 'Frequently asked questions',
    faqSubtitle: 'Everything you need to know about Open Telephony.',
    faqs: [
      {
        question: 'Can I keep my existing phone numbers?',
        answer:
          'Yes. Bring your own numbers and keep them exactly where they are — no porting required. You can also provision new local, toll-free, or vanity numbers in 100+ countries from the dashboard in minutes.',
      },
      {
        question: 'How fast can we go live?',
        answer:
          'Most teams are taking live calls the same day. Provision numbers, build your call flows in the visual editor, and connect your CRM in minutes — no telecom engineer or professional-services contract required. Bring your own carrier via SIP trunking if you prefer.',
      },
      {
        question: "What happens when the AI can't handle a call?",
        answer:
          'It warm-transfers to the right human with the full transcript and context attached, so your team picks up knowing exactly what the caller needs.',
      },
      {
        question: 'Does it support SMS as well as voice?',
        answer:
          'Yes. Two-way SMS lives in the same shared inbox as your calls, voicemails, and contacts — with the full conversation history visible to the whole team and AI-drafted replies on tap.',
      },
      {
        question: 'Which CRMs and tools does it integrate with?',
        answer:
          'Calls, texts, recordings, and summaries sync natively to Salesforce, HubSpot, Pipedrive, Zendesk, and 100+ other tools. For anything custom, use the REST API or our MCP server.',
      },
      {
        question: 'Is it secure and compliant?',
        answer:
          'Yes. Open Telephony is SOC 2 Type II certified and GDPR and HIPAA compliant, with call recording controls and full audit logs.',
      },
    ],
  },
  ar: {
    heroTitle: 'نظام الهاتف بالـ AI اللي يقدر على كل شيء',
    heroSubtitleA:
      'كل ميزة في هاتف الأعمال الحديث — مكالمات، أرقام، IVR، SMS، تسجيل، تحليلات.',
    heroSubtitleB: 'مع AI يرفع السمّاعة فعلاً، يحل، ويوجّه المكالمة.',
    worksWithLabel: 'يتزامن مع',
    bubbleTopic: 'تغيير حجز · جاري الحل',
    bentoTitle: 'كل اللي يفعله نظام الهاتف الحديث — وAI يتولّى المكالمة.',
    bentoSubtitle:
      'في Open Telephony كل ميزة تتوقعها من منصة هاتف أعمال، ومعها موظف استقبال AI يرد ويحل ويوجّه على مدار الساعة.',
    benefitsTitle: 'صوت الـ AI اللي كان ينقص نظام هاتفك',
    benefitsSubtitle: 'محادثات طبيعية، بأي لغة، على كل خط — مدعومة بـ Agent 5.',
    benefits: [
      {
        title: 'أرقام دولية',
        description:
          'أرقام هاتف محلية في +١٠٠ دولة. عميلك ما يدفع رسوم مكالمة دولية، وأنت ما تنقل رقماً واحداً.',
      },
      {
        title: 'أصوات مخصصة واستنساخ صوت',
        description:
          'استنسخ صوت علامتك، أو ابنِ صوتاً من الصفر. والـ AI عندك يتكلم بالصوت اللي تريده بالضبط.',
      },
      {
        title: 'تزامن عالي',
        description:
          'يتحمّل حتى ٥٠٠ مكالمة متزامنة. لا نغمة مشغول، ولا موسيقى انتظار — يتوسّع فوراً في أكثر ساعاتك ازدحاماً.',
      },
      {
        title: 'دعم بعدة لغات',
        description: 'كلّم كل متصل بلغته، بنطق أهل اللغة، في +٦ لغات.',
      },
      {
        title: 'SIP Trunking وبنية أساسها API',
        description:
          'اربطه عبر SIP trunking أو API. يشتغل مع أي نظام هاتف — أو ابنِ مسارات مكالمات مخصصة من الصفر.',
      },
    ],
    checklistEyebrow: 'الميزات كاملة',
    checklistTitle: 'كل ميزة، في نظام هاتف واحد.',
    checklistSubtitle:
      'القائمة الكاملة — كل اللي تجده موزّعاً على أفضل أدوات هاتف الأعمال، مجموعاً هنا في منصة وحدة أساسها AI.',
    faqTitle: 'الأسئلة الشائعة',
    faqSubtitle: 'كل اللي تحتاج تعرفه عن Open Telephony.',
    faqs: [
      {
        question: 'أقدر أحتفظ بأرقامي الحالية؟',
        answer:
          'نعم. أرقامك تبقى في مكانها بالضبط — بدون نقل. وإذا أردت أرقاماً جديدة، محلية أو مجانية أو مميزة، تجهّزها في +١٠٠ دولة من لوحة التحكم في دقائق.',
      },
      {
        question: 'كم يستغرق تشغيل النظام؟',
        answer:
          'أغلب الفرق تستقبل مكالمات حقيقية في اليوم نفسه. جهّز الأرقام، وارسم مسارات المكالمات في المحرر المرئي، ووصّل الـ CRM عندك — دقائق، وبدون مهندس اتصالات ولا عقد خدمات احترافية. وإذا أردت الاستمرار مع مشغّل الاتصالات الحالي، وصّله عبر SIP trunking.',
      },
      {
        question: 'وش يحدث لو المكالمة أكبر من الـ AI؟',
        answer:
          'يحوّلها للشخص المناسب، ومعها نص المحادثة والتفاصيل كاملة. فريقك يرفع السمّاعة وهو يعرف وش صار ووش يحتاجه المتصل بالضبط.',
      },
      {
        question: 'هل يدعم SMS كما يدعم الصوت؟',
        answer:
          'نعم. الـ SMS الثنائي في الصندوق المشترك نفسه مع مكالماتك وبريدك الصوتي وجهات اتصالك — السجل كامل أمام الفريق، وردود يكتبها AI جاهزة عند الطلب.',
      },
      {
        question: 'مع أي أنظمة CRM وأدوات يتكامل؟',
        answer:
          'المكالمات والرسائل والتسجيلات والملخصات تتزامن أصلياً مع Salesforce، HubSpot، Pipedrive، Zendesk، و+١٠٠ أداة أخرى. ولأي شيء خارج الجاهز، عندك REST API أو الـ MCP server الخاص بنا.',
      },
      {
        question: 'هل المنصة آمنة ومتوافقة مع المعايير؟',
        answer:
          'نعم. Open Telephony حاصل على شهادة SOC 2 Type II ومتوافق مع GDPR وHIPAA، مع أدوات تحكّم في تسجيل المكالمات وسجلات تدقيق كاملة.',
      },
    ],
  },
} as const;

export default function OpenTelephonyPage() {
  const { locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const bentoCards = locale === 'ar' ? BENTO_CARDS_AR : BENTO_CARDS_EN;
  const capabilityGroups =
    locale === 'ar' ? CAPABILITY_GROUPS_AR : CAPABILITY_GROUPS_EN;

  return (
    <>
      <SubpageHero
        eyebrow="Open Telephony"
        title={c.heroTitle}
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/lady-on-the-phone.mp4"
        bubblePosition="top-right"
        worksWithLabel={c.worksWithLabel}
        worksWith={[
          { src: '/logos/logo-salesforce.svg', alt: 'Salesforce' },
          { src: '/logos/logo-hubspot.svg', alt: 'HubSpot' },
          { src: '/logos/logo-zendesk.svg', alt: 'Zendesk' },
          { src: '/logos/logo-intercom.svg', alt: 'Intercom' },
        ]}
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleCall
                status="live"
                caller="+1 (415) 555-0148"
                topic={c.bubbleTopic}
              />
            ),
          },
        ]}
      />

      <BentoGrid
        eyebrow="Open Telephony"
        title={c.bentoTitle}
        subtitle={c.bentoSubtitle}
        cards={bentoCards}
      />

      <BenefitsSection
        title={c.benefitsTitle}
        subtitle={c.benefitsSubtitle}
        learnMoreLink="#faq"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: c.benefits[0].title,
            description: c.benefits[0].description,
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/numbers.mp4',
            imagePosition: 'right',
          },
          {
            title: c.benefits[1].title,
            description: c.benefits[1].description,
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/cloning.mp4',
            imagePosition: 'left',
          },
          {
            title: c.benefits[2].title,
            description: c.benefits[2].description,
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/concurrency.mp4',
            imagePosition: 'right',
          },
          {
            title: c.benefits[3].title,
            description: c.benefits[3].description,
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/languages.mp4',
            imagePosition: 'left',
          },
          {
            title: c.benefits[4].title,
            description: c.benefits[4].description,
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/api.mp4',
            imagePosition: 'right',
          },
        ]}
      />

      {/* Complete capability checklist */}
      <section className="bg-white py-16 md:py-32">
        <div className="container">
          <div className="mb-14 max-w-2xl md:mb-20">
            <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {c.checklistEyebrow}
            </p>
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {c.checklistTitle}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.checklistSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {capabilityGroups.map((group) => (
              <div key={group.title}>
                <h3 className="mb-5 text-sm font-medium tracking-wide text-neutral-900 uppercase">
                  {group.title}
                </h3>
                <ul className="space-y-3">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm leading-snug text-neutral-600 md:text-[15px]"
                    >
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500"
                        strokeWidth={2.5}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CustomerVideos />

      {/* FAQ */}
      <FaqSection title={c.faqTitle} subtitle={c.faqSubtitle} items={c.faqs} />

      <Testimonials />
    </>
  );
}
